import * as fb from './firebase.js'
import moment from 'moment'
import Joi from 'joi'
import { resize } from './image-resize.js'
import * as firebaseui from 'firebaseui'
const momentDateFormat = 'MMMM Do YYYY, h:mm:ss a'
const missingDate = '- - -'
const PACKINGLISTTEMPLATES = 'packing-list-templates'
const rux = {
  /**
   * Login via email
   * @param {string} email - the email to authenticate against firebase
   * @param {string} password - the password to auth against firebase
   * @return {Object} an object containing a firebase user profile
   */
  loginEmail: async (email, password) => {
    return fb.auth.signInWithEmailAndPassword(email, password)
  },
  /**
   * Creates an account in Firebase via email
   * @param {string} email - the email to create account with firebase
   * @param {string} password - the password to create acccount firebase
   */
  createAccountWithEmail: async (email, password) => {
    return fb.auth.createUserWithEmailAndPassword(email, password)
  },
  resetPasswordViaEmail: async (email) => {
    return fb.auth.sendPasswordResetEmail(email)
  },
  /**
   * Creates a user profile under the UID in firebase - Can add any fields and they will immediately be added to firebase
   * @param {Object} user - the details of the user to create
   * @param {string} user.email - the user's email
   * @param {string} user.name - the user's name
   *
   */
  createAndUpdateUserProfile: async (user) => {
    return fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('profile')
      .update(user)
  },
  /**
   * Retrieve the user profile from the database
   * @return {Object} An object containing all the user details if it exists or empty if not
   */
  getUserProfile: async () => {
    const snapshot = await fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('profile')
      .once('value')
    return snapshot ? snapshot.val() : {}
  },
  /**
   * Gets all the ruxes stored for the current user.
   */
  getRuxFromDB: async () => {
    // Get the values from firebase
    const snapshot = await fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('rux')
      .once('value')
    const values = snapshot.val()
    // If there are responses evaulate them
    if (values) {
      // Firebase returns an object with keys, not an Array of objects.
      // Convert it to an array of usable objects here using Object.keys
      return Object.keys(values).map((rux) => {
        return values[rux]
      }).map((rux) => {
        const friendlyValues = addFriendlyFields(rux)
        friendlyValues.packingListItems = makePackingListItem(rux.packingList)
        delete friendlyValues.packingList
        return friendlyValues
      })
    } else { // return an empty array if there is nothing found so it doesn't freak out.
      return []
    }
  },
  getSingleRuxFromDB: async (serialNumber) => {
    const snapshot = await fb.db.ref('users').child(fb.auth.currentUser.uid).child('rux').child(serialNumber).once('value')
    const value = snapshot.val()
    if (value) {
      const friendlyValues = addFriendlyFields(value)
      if (value.packingList) {
        friendlyValues.packingListItems = makePackingListItem(value.packingList, value.packingListChecked)
      } else {
        friendlyValues.packingList = {}
      }
      delete friendlyValues.packingList
      return [friendlyValues]
    } else {
      return []
    }
  },
  checkRuxExistsInGlobalList: async (serialNumber) => {
    const snapshot = await fb.db.ref('global-rux-list').child(serialNumber).once('value')
    const value = snapshot.val()
    return !!value
  },
  checkRuxHasReferralLink: async (serialNumber) => {
    const snapshot = await fb.db.ref('global-rux-list').child(serialNumber).child('referralLink').once('value')
    const value = snapshot.val()
    return value
  },
  checkRuxRegisteredToEmail: async (serialNumber) => {
    const snapshot = await fb.db.ref('global-rux-list').child(serialNumber).child('registeredToEmail').once('value')
    const value = snapshot.val()
    return value
  },
  /**
   * Delete's a rux from the users object in firebase
   * @param {string} serialNumber - the serial number to delete it from
   */
  deleteSingleRuxFromDB: async (serialNumber) => {
    const ref = fb.db.ref('users').child(fb.auth.currentUser.uid).child('rux').child(serialNumber)
    const snapshot = await ref.once('value')
    const value = snapshot.val()

    if (value) {
      await ref.remove()
      return true
    } else {
      return false
    }
  },
  /**
   * Delete's a rux from the global list object in firebase
   * @param {string} serialNumber - the serial number to delete it from
   */
  deleteRuxFromGlobalList: async (serialNumber) => {
    await fb.db.ref('global-rux-list').child(serialNumber).remove()
    return true
  },
  /**
   * Add a new rux to the db. This sets the UID based on whoever is currently logged in.
   * @param {Object} newRux - An object containing all the fields to be stored on the db.
   * @param {string} newRux.serialNumber - the serial Number
   * @param {string} newRux.shortDescription - A short description of the rux
   * @param {string} newRux.fullDescription - A longer description of the rux.
   */
  addRux: async (newRux) => {
    // Add a field with the current date and time.
    newRux.registeredOn = moment().format()
    // Set the ref for firebase to be the user
    return fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('rux')
      .child(newRux.serialNumber)
      .update(newRux)
  },
  /**
   * Adds a rux to the global list to prevent multiple registrations
   * @param {string} serialNumber - The Serial Number to add to the list
   * @returns {Promise}
   */
  addRuxToGlobalList: async (serialNumber) => {
    // Add a little bit of data to the object so its obvious who registered it and when
    return fb.db.ref('global-rux-list').child(serialNumber).update({
      registeredOn: moment().format(),
      registeredTo: fb.auth.currentUser.uid,
      registeredToEmail: fb.auth.currentUser.email
    })
  },
  updateReferralLinkRuxGlobalList: async (serialNumber, link) => {
    return fb.db.ref('global-rux-list').child(serialNumber).update({
      referralLink: link
     })
  },
  // Update a rux
  updateRux: async (rux) => {
    // Generate the date the Rux was last updated
    rux.lastUpdatedOn = moment().format()
    // Validate the rux and strip out any thing not needed here
    const validatedRux = validate(rux)
    // Set the ref for firebase to be the user
    return fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('rux')
      .child(rux.serialNumber)
      .update(validatedRux)
  },
  /**
   * Add a packing list item to a rux
   * @param {Object} params - The object that contains all the data
   * @param {string} params.item - The item to add
   * @param {string} params.serialNumber - The serial number to add it to.
   */
  addPackingListItem: async (params) => {
    const { item, serialNumber } = params
    return fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('rux')
      .child(serialNumber)
      .child('packingList')
      .push(item)
  },
  /**
   * Delete a packing list item
   * @param {object} params - The object containing all the data
   * @param {string} params.item - the item to delete from the db.
   * @param {string} params.serialNumber - the serial number to delete it from
   */
  deletePackingListItem: async (params) => {
    // Unpack the params
    const { item, serialNumber } = params
    // Get the packing list items from the db from the associated RUX
    const ref = fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('rux')
      .child(serialNumber)
      .child('packingList')
    // Find the the item in the DB
    const values = await ref.once('value')
    const snapshot = values.val()
    if (snapshot) {
      const itemToDelete = Object.keys(snapshot).map((packingListItem) => {
        return {
          key: packingListItem,
          item: snapshot[packingListItem]
        }
      }).find((packingListItem) => {
        return packingListItem.item === item.value
      })

      // Delete the associated checkmark
      const checkedRef = fb.db.ref('users')
        .child(fb.auth.currentUser.uid)
        .child('rux')
        .child(serialNumber)
        .child('packingListChecked')

      checkedRef.child(itemToDelete.key).remove()
      // Actually delete the item here
      return ref.child(itemToDelete.key).remove()
    } else {
      throw new Error('Item does not exist in database')
    }
  },
  updatePackingListItem: (params) => {
    const { item, serialNumber } = params
    return fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('rux')
      .child(serialNumber)
      .child('packingListChecked')
      .update({ [item.key]: item.value })
  },
  /**
   * Delete all packing list items from a RUX
   * @param {string} serialNumber - the serial number to delete it from
   */
  deleteAllPackingListItems: async (serialNumber) => {
    // Unpack the params
    // Get the packing list items from the db from the associated RUX
    const ref = fb.db.ref('users')
      .child(fb.auth.currentUser.uid)
      .child('rux')
      .child(serialNumber)
      .child('packingList')

      ref.remove()
  },
  /**
  * Checks whether someone is logged in.
  */
  isAuth: () => {
    return !!fb.auth.currentUser
  },
  // Returns the uid of the current user
  getAuthUID: () => {
    return fb.auth.currentUser.uid
  },
  // Signs out
  logout: () => {
    return fb.auth.signOut()
  },
  /**
   *  Gets the AuthUI from firebase. @see https://firebase.google.com/docs/auth/web/firebaseui
   * @param {string} successURL - where to send the user after the request is successful
   * @param {string} container - an Id where the FirebaseUI is started in.
   */
  getAuthUIConfig: async (successURL, container) => {
    // Check to see if the system is running in test mode. This is necessary for running the tests as the "browser" freaks out
    await fb.auth.setPersistence(process.env.NODE_ENV === 'test' ? fb.firebase.auth.Auth.Persistence.NONE : fb.firebase.auth.Auth.Persistence.LOCAL)
    // Create it the instance
    const uiConfig = new firebaseui.auth.AuthUI(fb.auth)
    // Fire it up here
    uiConfig.start(container, {
      signInFlow: 'popup',
      signInOptions: [
        fb.firebase.auth.GoogleAuthProvider.PROVIDER_ID,
        fb.firebase.auth.FacebookAuthProvider.PROVIDER_ID
      ],
      callbacks: {
        signInSuccessWithAuthResult: (authResult) => {
          // successResponse is necessary because making signInSuccessWithAuthResult async always causes it to return true.
          // Returning true is not desired as it will redirect the user before the user profile is created, hence why we manually
          const successResponse = async () => {
            const userProfile = await rux.getUserProfile()
            if (!userProfile) {
               rux.createAndUpdateUserProfile({ email: authResult.user.email, name: authResult.user.displayName })
            }
            window.location.href = successURL
          }
          successResponse()
        },
      }
    })
  },
  /**
   * Upload an image to the datastore - Currently using firebase storage
   * @param {object} imageDetails- an obejct contain all the details
   * @param {string} imageDetails.serialNumber - the serial number to add the image to
   * @param {string} imageDetails.image - The file to upload
   */
  uploadImage: async (imageDetails) => {
    const { image, serialNumber } = imageDetails
    const imageName = sanitizeImageName(image.name)
    // Resize the image here.
    const newImage = await resize.resize({ image, maxWidth: 900, imageQuality: 0.5 })
    // Create the ref here
    const ref = fb.storage.ref(fb.auth.currentUser.uid).child('rux').child(serialNumber).child(imageName)
    // Return the ref to get the URL later on for displaying.
    return ref.put(newImage)
  },
  /**
   * Get images from the datastore NB. returns just the imageURL not the image - Currently using firebase storage
   * @param {string} serialNumber - the serial number to get the images from
   * @returns {Promise[]} - Returns an array of strings that contain the URL for the image, not the actual image.
   */
  getImages: async (serialNumber) => {
    // Use the storage object to the the images
    const ref = fb.storage.ref(fb.auth.currentUser.uid).child('rux').child(serialNumber)
    // Get a list of them here
    const snapshot = await ref.listAll()
    return Promise.all(snapshot.items.map(snap => {
      return fb.storage.ref(snap.location.path).getDownloadURL()
    }))
  },
  /**
   * Delete an image from the datastore
   * @param {object} imageDetails - An object contain all the properties
   * @param {string} imageDetails.serialNumber - The Serial number to delete the image for
   * @param {string} imageDetails.imageName - the Name of the image to delete
   */
  deleteImage: async (imageDetails) => {
    const { serialNumber, imageName } = imageDetails
    // Find the image.
    const ref = fb.storage.ref(fb.auth.currentUser.uid).child('rux').child(serialNumber).child(imageName)
    // Delete it here.
    return ref.delete()
  },
  /**
   * Validate a serial number. This use Firebase functions to validate whether an encoded string is actually a serial number or a bunch of nothing
   * @see https://github.com/AndrewMacNaughton/ruxx-functions
   * @param {string} encodedSerialNumber - The serial number to check. This should be encoded using the ruxx-functions libraries Encryptor.js file
   * @returns {Promise<Object>} Returns whether it is a valid serialnumber or not and details of the RUX if true
   */
  validateSerialNumber: async (encodedSerialNumber) => {
    // Use Firebase functions to check this.
    const decryptSerial = fb.functions.httpsCallable('decryptSerialNumber')
    return decryptSerial({ serialNumber: encodedSerialNumber })
  },
  /**
   * Validates whether the pin number supplied on registration is valid. Handle in Firebase functions so its harder to crack
   * @see https://github.com/AndrewMacNaughton/ruxx-functions
   * @param {string} pin - The pin number to check
   * @param {string} serialNumber - Need this to validate the pin as well.
   */
  confirmPin: async (pin, serialNumber) => {
    // Call the firebase function here
    const confirmPin = fb.functions.httpsCallable('validatePinCode')
    return confirmPin({ pin, serialNumber })
  },
  /**
   * Add a user to the marketing store once they have signed up
   */
  addEmailToMarketingStore: async () => {
    const email = {
      [fb.auth.currentUser.uid]: fb.auth.currentUser.email
    }
    return fb.db.ref('marketing-list').child('emails').update(email)
  },
  /**
   * Get all the packing list templates for a user
   * @returns {object[]} - An array of objects containing all the important info about the packing list template
   */
  getPackingListTemplates: async () => {
    const ref = await fb.db.ref(PACKINGLISTTEMPLATES).child(fb.auth.currentUser.uid)
    const values = await ref.once('value')
    const snapshot = values.val()
    // Check to see if there are any there
    if (snapshot) {
      return Object.keys(snapshot).map((template) => {
        const item = snapshot[template]
        item.dbKey = template
        return item
      }).map((template) => { // Make them into a nice array if there are any, with packingListItems
        template.packingListItems = makePackingListItem(template.items)
        return template
      })
    } else {
      return [] // Returns an empty array if none there
    }
  },
  /**
   * Get one packing list template by name
   * @param {string} name - The name of the template item
   * @returns {object} all the details of the packing list item or undefined
   */
  getPackingListTemplateItem: async (name) => {
    // Get all of them and find the one being searched for.
    const values = await rux.getPackingListTemplates()
    return values.find((template) => {
      return template.name.toLowerCase() === name.toLowerCase()
    })
  },
  /**
   * Get a public packing list
   * @param {string} uid - The UID of the user who created it. This is passed from the route.
   * @param {string} dbKey - the dbKey of the packing list template. This is also passed from the route
   * @returns {object[]} all the details of the packing list item or undefined
   */
  getPublicPackingListTemplateItem: async (uid, dbKey) => {
    const ref = fb.db.ref(PACKINGLISTTEMPLATES).child(uid).child(dbKey)
    const values = await ref.once('value')
    const snapshot = values.val()
    // Make sure there is something there
    if (snapshot) {
      // Add a nice array of the items.
      snapshot.packingListItems = makePackingListItem(snapshot.items)
      // Return it as an Array
      return [snapshot]
    } else {
      return [] // Return an empty array if it doesn't exist
    }
  },
  /**
   * Get the images(URL ONLY!) for a public packing list. NB. This doesn't return images, just the link to download them
   * @param {string} uid - The UID of the user who created it. This is passed from the route.
   * @param {string} dbKey - the dbKey of the packing list template. This is also passed from the route
   * @returns {String[]} Returns an array of strings with the download URLS all the details of the packing list item or undefined
   */
  getPublicPackingListTemplateImages: async (uid, dbKey) => {
    const ref = await fb.storage.ref(uid).child(PACKINGLISTTEMPLATES).child(dbKey)
    const snapshot = await ref.listAll()
    return Promise.all(snapshot.items.map(snap => {
      return fb.storage.ref(snap.location.path).getDownloadURL()
    }))
  },
  /**
   * Add a packing list template
   * @param {object} params - the object containing everything
   * @param {string} params.name  the name of the template
   * @returns {string} returns the key of the recently created template.
   */
  addPackingListTemplate: async (params) => {
    const { name } = params
    // Add it here including who added it. This is used on the public side later and allows the firebase rules to be tighter
    const result = await fb.db.ref(PACKINGLISTTEMPLATES).child(fb.auth.currentUser.uid).push({ name: name, createdBy: fb.auth.currentUser.email })
    // Return the key of the item just added
    return result.getKey()
  },
  /**
   * Update a packing list template
   * @param {object} params - Containing object
   * @param {string} params.name - the name to update
   * @param {string} params.key - the key to update. This needs to be correct or it will update the wrong record
   * @param {string} params.description - The description to update.
   */
  updatePackingListTemplate: async (params) => {
    const { name, key, description, isPublic } = params
    return fb.db.ref(PACKINGLISTTEMPLATES).child(fb.auth.currentUser.uid).child(key).update({ name: name, description, isPublic })
  },
  /**
   * Delete the packing list template here
   * @param {object} params object containing all the details
   * @param {string} params.dbKey - the key to remove
   */
  deletePackingListTemplate: async (params) => {
    const { dbKey } = params
    return fb.db.ref(PACKINGLISTTEMPLATES).child(fb.auth.currentUser.uid).child(dbKey).remove()
  },
  /** Adds a packing list item to the packing list template
   * @param {object} params - The object containing the parameters
   * @param {string } params.item - the item to add to the template
   * @param {key} params.key - the key of the Template to add the packing list to
   */
  addPackingListItemToTemplate: async (params) => {
    const { item, key } = params
    const result = fb.db.ref(PACKINGLISTTEMPLATES).child(fb.auth.currentUser.uid).child(key).child('items').push(item)
    return result.getKey()
  },
  /** Delete a packing list item from a template
   * @param {object} params - The object containing the parameters
   * @param {string} params.key - the key of the template to delete from
   * @param {string} params.item - the name/item to delete
   */
  deletePackingListItemFromTemplate: async (params) => {
    const { key, item } = params
    const ref = fb.db.ref(PACKINGLISTTEMPLATES).child(fb.auth.currentUser.uid).child(key).child('items')
    const values = await ref.once('value')
    const snapshot = values.val()
    // Get all the items here.
    if (snapshot) {
      const itemToDelete = Object.keys(snapshot).map((packingListItem) => {
        return {
          key: packingListItem,
          item: snapshot[packingListItem]
        }
      }).find((packingListItem) => { // Find the item in the array here
        return packingListItem.item === item.value
      }).key
      // Using the key found above delete it here.
      return ref.child(itemToDelete).remove()
    } else {
      throw new Error('Item does not exist in database')
    }
  },
  /**
   * Adds an image to the datastore associated with the template
   * @param {object} params - The object containing the parameters
   * @param {File} params.image - the image to upload
   * @param {string} params.dbKey - the key of the template that this image is associated with
   */
  addPackingListTemplateImage: async (params) => {
    const { image, dbKey } = params
    const imageName = sanitizeImageName(image.name)
    // Resize the image so they aren't enormous here. Doesnt need to be high quality for this.
    const newImage = await resize.resize({ image, maxWidth: 900, imageQuality: 0.5 })
    const ref = fb.storage.ref(fb.auth.currentUser.uid).child(PACKINGLISTTEMPLATES).child(dbKey).child(imageName)
    return ref.put(newImage)
  },
  /**
   * Deletes an image from the datastore associated with the template
   * @param {object} params - The object containing the parameters
   * @param {File} params.imageName - the image to delete
   * @param {string} params.dbKey - the key of the template that this image is associated with
   */
  deletePackingListTemplateImage: async (params) => {
    const { imageName, dbKey } = params
    const ref = fb.storage.ref(fb.auth.currentUser.uid).child(PACKINGLISTTEMPLATES).child(dbKey).child(imageName)
    return ref.delete()
  },
  /**
   * Get the images(URL ONLY!) for a packing list. NB. This doesn't return images, just the link to download them
   * @param {string} dbKey - the dbKey of the packing list template. This is passed from the route
   * @returns {String[]} Returns an array of strings with the download URLS all the details of the packing list item or undefined
   */
  getPackingListTemplateImages: async (dbKey) => {
    const ref = await fb.storage.ref(fb.auth.currentUser.uid).child(PACKINGLISTTEMPLATES).child(dbKey)
    const snapshot = await ref.listAll()
    return Promise.all(snapshot.items.map(snap => {
      return fb.storage.ref(snap.location.path).getDownloadURL()
    }))
  }

}
const addFriendlyFields = (value) => {
  const friendlyValues = { ...value }
  // Do some formatting here for the friendly date etc.
  friendlyValues.friendlyDateRegisteredOn = friendlyValues.registeredOn ? moment(friendlyValues.registeredOn).format(momentDateFormat) : missingDate
  friendlyValues.friendlyLastUpdatedOn = friendlyValues.lastUpdatedOn ? moment(friendlyValues.lastUpdatedOn).format(momentDateFormat) : missingDate
  friendlyValues.friendlyLocation = friendlyValues.location && friendlyValues.location.latitude && friendlyValues.location.longitude ? friendlyValues.location.latitude + ',' + friendlyValues.location.longitude : null
  return friendlyValues
}

const makePackingListItem = (items, checkedItems) => {
  if (!items) {
    return []
  }
  if (!checkedItems) checkedItems = {}
  return Object.keys(items).map((item) => {
    return {
      value: items[item],
      checked: checkedItems[item] || false,
      key: item
    }
  })
}

// Strip out special characters that break firebase
const sanitizeImageName = (imageName) => {
  return imageName.replace(/[@ ()*;<>{}/[\]]/g, '')
}

// A function that uses Joi to validate the object
const validate = (object) => {
  const rux = { ...object }
  const schema = Joi.object({
    serialNumber: Joi.string().required(),
    lastUpdatedOn: Joi.string().allow(''),
    registeredOn: Joi.string().allow(''),
    shortDescription: Joi.string().allow(''),
    fullDescription: Joi.string().allow(''),
    packingList: Joi.object(),
    location: Joi.object(),
    manualLocation: Joi.string().allow('')
  })
  const { error, value } = schema.validate(rux, {
    stripUnknown: true
  })
  if (!error) {
    return value
  } else {
    throw new Error(error)
  }
}

export {
  rux
}
